import React, {
	Dispatch,
	useCallback,
	useMemo,
	useRef,
	useState,
	useLayoutEffect,
	useEffect,
} from "react";
import { UseTranslationResponse } from "react-i18next";
import { SortType } from "rsuite-table";
import { isString } from "lodash";
import { Nullable } from "uikit";

import { Executor, Language, TaxiService } from "../../../../../services";
import Parking from "../../../../../services/Parking2";
import {
	useColumns,
	useVisibleColumns,
	useWidths,
} from "../../../../../hooks/useTableSettings";
import { ParkingFilter } from "../../../../../redux/reducers/OrdersPage/Filters";
import { parkingFilterAllowsId } from "../../../../../redux/reducers/Orders/Parking/filter";
import { TableSettings } from "../../../..";
import { useTableOptions, UseTableOptions } from "../../../../LightTable";
import CompactTable from "../../../../CompactTable";
import { StyledGrid, Colors } from "../../../../common";
import { ParkingsWidget } from "../..";
import { useTypedSelector } from "../../../../../redux/store";
import { useAppContext } from "../../../../../context";
import { OrderSource } from "../../../../../types";
import { ExecutorStatus } from "../../../../../services/Executor";
import { sortByNumberOrString } from "../../../../../utils";
import useParkingActions from "../../hooks/useParkingActions";

import columnBase, {
	ColumnId,
	ColumnId as LocalColumnId,
	defaultColumnIds,
} from "./columns";

const ModelTable: React.FC<ModelTable.Props> = ({
	selectedId,
	loading,
	data,
	nameColuumnsGroup,
	//
	onChangeSelectedId,
	editorTable,
	onChangeTable,
	tableFooter,
	onChangeIsEditModal,
	onShowParkingModal,
}) => {
	const settingsRef = useRef<HTMLDivElement | null>(null);
	const { getCompanyIdByTaxiServiceId, getTaxiServiceName, getCompanyName } =
		useAppContext();
	const { criteria: filters } = useTypedSelector(
		(state) => state.orders.parking.filter,
	);
	const executors = useTypedSelector(
		(state) => state.globalState.executors.data.subscription.basic,
	);
	const { onChangeFilter } = useParkingActions();
	const STABILITY_HEIGHT_ROW = 32;

	const [isSort, setIsSort] = useState<boolean>(false);

	const _nameColuumnsGroup = useMemo(
		() => nameColuumnsGroup,
		[nameColuumnsGroup],
	);

	useEffect(() => {
		if (filters.taxiServiceIdsPosition.length && !isSort) setIsSort(true);
	}, [filters.taxiServiceIdsPosition.length, isSort]);

	const {
		tableRef,
		lang,
		onKeyDown,
		onKeyUp,
		onScroll,
		setDataLength,
		widthScroll,
		heightScroll,
		sort,
		onSort,
	} = useTableOptions({
		value: editorTable,
		setValue: onChangeTable,
		heightRow: STABILITY_HEIGHT_ROW,
		saveSlot: _nameColuumnsGroup.reduxLocal,
		tableId: _nameColuumnsGroup.name,
	});

	useLayoutEffect(() => {
		setDataLength(data.length);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.length]);

	const scrollPos = useMemo(
		() => ({ x: widthScroll, y: heightScroll }),
		[heightScroll, widthScroll],
	);

	const columns = useColumns(_nameColuumnsGroup.name);
	const { columnIds, setColumnIds } = useVisibleColumns(
		_nameColuumnsGroup.reduxLocal,
		_nameColuumnsGroup.name,
	);
	const { widths, setWidths } = useWidths(
		_nameColuumnsGroup.reduxLocal,
		_nameColuumnsGroup.name,
	);

	const onRowDoubleClick = useCallback(
		(rowData) => {
			onChangeSelectedId(rowData.id);
			onChangeIsEditModal(true);
			onShowParkingModal(true);
		},
		[onChangeIsEditModal, onChangeSelectedId, onShowParkingModal],
	);

	const onRowSelect = useCallback(
		(rowData) => {
			if (rowData.id === selectedId) {
				onChangeSelectedId(0);
			} else {
				onChangeSelectedId(rowData.id);
			}
		},

		[onChangeSelectedId, selectedId],
	);

	const modelTableColumns = useMemo<ModelTable.Column[]>(() => {
		const _columnIds = columnIds as LocalColumnId[];

		return columns
			.map((column) => {
				const id = column.id as LocalColumnId;

				const retval = {
					id,
					width: widths[id],
					visible: _columnIds?.includes(id) ?? false,
				};
				return retval;
			})
			.sort((leftColumn, rightColumn) => {
				const leftIndex = _columnIds.indexOf(leftColumn.id);
				const rightIndex = _columnIds.indexOf(rightColumn.id);
				return leftIndex - rightIndex;
			});
	}, [columns, widths, columnIds]);

	const tableRowClassName = useCallback(
		(rowData) => {
			if (rowData?.id === selectedId) return "founded";
			if (!rowData?.active) return "not-active";
			return "";
		},
		[selectedId],
	);

	const tableContainerRef = useRef<HTMLDivElement | null>(null);
	const [tableViewportWidth, setTableViewportWidth] = useState(0);

	useLayoutEffect(() => {
		if (!tableContainerRef.current) return () => {};

		const updateSize = () => {
			const width = tableContainerRef.current?.clientWidth ?? 0;
			setTableViewportWidth(width);
		};

		updateSize();

		const resizeObserver = new ResizeObserver(updateSize);
		resizeObserver.observe(tableContainerRef.current);

		return () => resizeObserver.disconnect();
	}, []);

	const aliasesColumnDisplayWidth = useMemo(() => {
		const visibleColumns = modelTableColumns.filter(
			(column) => column.visible,
		);
		const aliasesColumn = visibleColumns.find(
			(column) => column.id === ColumnId.Aliases,
		);
		if (!aliasesColumn) {
			return widths?.[ColumnId.Aliases] ?? 120;
		}

		const totalWidth = visibleColumns.reduce(
			(sum, column) => sum + column.width,
			0,
		);
		const isLastColumn =
			visibleColumns[visibleColumns.length - 1]?.id === ColumnId.Aliases;
		const isMustWithColumnThanViewport =
			tableViewportWidth > 0 && totalWidth < tableViewportWidth;

		if (isLastColumn && isMustWithColumnThanViewport) {
			return tableViewportWidth + aliasesColumn.width - totalWidth;
		}

		return aliasesColumn.width;
	}, [modelTableColumns, tableViewportWidth, widths]);

	const rowHeight = useCallback(
		(rowData) => {
			const defaultHeight = STABILITY_HEIGHT_ROW;
			let height = defaultHeight;

			const taxiServiceDescription = rowData?.description;
			if (taxiServiceDescription && isString(taxiServiceDescription)) {
				const fontSize = 13;
				const fontBetween = 3;
				const lineHeight = fontSize + fontBetween;
				const padding = 28;
				const lines = taxiServiceDescription.split("\n").length;
				height = Math.max(height, lines * lineHeight + padding);
			}

			// Expand row so "Позывные водителей" wraps instead of clipping.
			const queueLen = Array.isArray(rowData?.executorsQueue)
				? rowData.executorsQueue.length
				: 0;
			if (queueLen > 0) {
				const aliasesWidth = Math.max(aliasesColumnDisplayWidth, 1);
				// Approximate chip: callsign (+ optional class shortName) + gap.
				const chipWidth = 52;
				const perLine = Math.max(
					1,
					Math.floor(aliasesWidth / chipWidth),
				);
				const lines = Math.ceil(queueLen / perLine);
				const lineHeight = 28;
				const verticalPadding = 6;
				height = Math.max(height, lines * lineHeight + verticalPadding);
			}

			return height;
		},
		[aliasesColumnDisplayWidth],
	);

	const modelTableOnChangeColumns = useCallback(
		(columns: ModelTable.Column[]) => {
			setColumnIds(
				columns
					.filter((column) => column.visible)
					.map((column) => column.id),
			);
			setWidths(
				columns.reduce((accumulator, column) => {
					accumulator[column.id] = column.width;
					return accumulator;
				}, {} as Record<LocalColumnId, number>),
			);
		},
		[setColumnIds, setWidths],
	);

	const renderedColumns = useMemo(() => {
		const visibleColumns = modelTableColumns.filter(
			(column) => column.visible,
		);
		const totalWidth = visibleColumns.reduce(
			(sum, column) => sum + column.width,
			0,
		);

		return visibleColumns.map((column, index) => {
			const isLastColumn = index === visibleColumns.length - 1;
			const isMustWithColumnThanViewport =
				totalWidth < tableViewportWidth;
			const widthWithAvtoFix =
				tableViewportWidth > 0 &&
				isLastColumn &&
				isMustWithColumnThanViewport
					? tableViewportWidth + column.width - totalWidth
					: column.width;

			const node = columnBase[column.id]?.render({
				width: widthWithAvtoFix,
				language: lang,

				onResize: (width, columnId) => {
					const newColumns = modelTableColumns.map((columns) =>
						columns.id === columnId
							? { ...columns, width: width ?? 0 }
							: columns,
					);

					modelTableOnChangeColumns(newColumns);
				},
			});

			return node;
		});
	}, [
		modelTableColumns,
		tableViewportWidth,
		lang,
		modelTableOnChangeColumns,
	]);

	const filterValue = useMemo(() => {
		const parkings: Parking.Model[] = data ?? [];
		const itemsMap: Map<number, Parking.Model> = new Map();
		const exeMap: Map<number, Executor.Model> = new Map(
			executors.map((item) => [item.id, item]),
		);

		const query = filters.query?.toLocaleLowerCase?.() || "";

		for (const item of parkings) {
			if (item?.id) {
				let isPush = true;
				if (query && item?.name) {
					try {
						const rawName = item.name as unknown;
						if (typeof rawName === "string") {
							if (!rawName.toLocaleLowerCase().includes(query))
								isPush = false;
						} else {
							const vals = Object.values(
								(rawName as Record<string, string>) ?? {},
							).filter(Boolean);

							if (!vals.length) isPush = false;
							else {
								const some = vals.some((name) =>
									String(name)
										.toLocaleLowerCase()
										.includes(query),
								);
								if (!some) isPush = false;
							}
						}
					} catch (e: any) {
						console.error(e?.message);
					}
				}

				if (isPush) {
					const companyId = getCompanyIdByTaxiServiceId(
						item.taxiServiceId,
					);
					const taxiServiceAllowed = parkingFilterAllowsId(
						filters.taxiServiceIds,
						item.taxiServiceId,
					);
					const companyAllowed = companyId
						? parkingFilterAllowsId(filters.companyIds, companyId)
						: true;

					if (taxiServiceAllowed && companyAllowed) {
						let isAdd = !filters?.executorGroupIds?.length;

						const executorsQueue: Parking.Model["executorsQueue"] =
							[];

						if (item.executorsQueue?.length) {
							for (const eq of item.executorsQueue) {
								if (eq?.executor?.id) {
									const executor = exeMap.get(eq.executor.id);

									if (
										filters.executorGroupIds?.length &&
										!isAdd
									) {
										const groupId = executor?.group?.id;
										if (groupId) {
											isAdd =
												filters.executorGroupIds.includes(
													groupId,
												);
										}
									}

									const isByStatus = (
										params: ParkingFilter,
										exe?: Executor.Model,
									) => {
										if (!exe?.id) return false;

										if (params.online?.length) {
											if (
												exe?.online &&
												!params.online.includes(
													"online",
												)
											) {
												return false;
											}

											if (
												!exe?.online &&
												!params.online.includes(
													"offline",
												)
											) {
												return false;
											}
										}

										if (!params.statuses?.length) {
											return true;
										}

										let byStatus = true;
										if (
											!params.statuses.includes(
												exe.status,
											)
										) {
											byStatus = false;
										}

										if (byStatus) return true;

										if (
											params.statuses.includes(
												ExecutorStatus.ON_ORDER,
											)
										) {
											if (params.isYourOrderStatus) {
												if (
													!exe?.executorToOrders
														?.length
												) {
													byStatus = true;
													return true;
												}

												const source =
													exe?.executorToOrders.some(
														(o) =>
															o?.order?.source ===
															OrderSource.EXECUTOR,
													);

												byStatus = source;
												if (source) return true;
											}
											if (byStatus) return true;
											if (!params.isYourOrderStatus) {
												if (
													!exe?.executorToOrders
														?.length
												) {
													return true;
												}
												const source =
													exe?.executorToOrders.some(
														(o) =>
															o?.order?.source !==
															OrderSource.EXECUTOR,
													);
												byStatus = source;
												if (source) return true;
											}
											if (byStatus) return true;
										}
										if (byStatus) return true;
										if (
											exe.status !==
												ExecutorStatus.ON_ORDER &&
											params.isYourOrderStatus
										) {
											if (params.isYourOrderStatus) {
												if (
													!exe?.executorToOrders
														?.length
												) {
													return true;
												}

												const source =
													exe?.executorToOrders.some(
														(o) =>
															o?.order?.source !==
															OrderSource.EXECUTOR,
													);
												byStatus = source;
												if (source) return true;
											}
											if (byStatus) return true;
											byStatus =
												!exe?.executorToOrders?.length;
											if (byStatus) return byStatus;
										}

										return byStatus;
									};

									const workingAllowed =
										!filters.workingStatus?.length ||
										(executor?.isWorking
											? filters.workingStatus.includes(
													"working",
											  )
											: filters.workingStatus.includes(
													"notWorking",
											  ));

									if (
										workingAllowed &&
										isByStatus(filters, executor)
									) {
										executorsQueue.push({
											...eq,
											executor: executor?.id
												? {
														...executor,
														callSign:
															executor.alias,
												  }
												: eq?.executor,
										});
									}
								}
							}
						}

						if (isAdd) {
							itemsMap.set(item.id, { ...item, executorsQueue });
						}
					}
				}
			}
		}

		const items: Parking.Model[] = [];

		if (filters.taxiServiceIdsPosition.length) {
			for (const positionId of filters.taxiServiceIdsPosition) {
				itemsMap.forEach((item) => {
					if (item?.taxiServiceId === positionId) {
						items.push(item);
						itemsMap.delete(item.id);
					}
				});
			}
		}

		if (itemsMap.size) {
			itemsMap.forEach((item) => {
				items.push(item);
			});
		}

		if (["company", "taxiService"].includes(sort.dataKey) && !isSort) {
			const isAscending = sort?.sortType?.toLowerCase() === "asc";
			const sortOrder = isAscending ? 1 : -1;
			if (sort.dataKey === ColumnId.Company) {
				const data = items.sort((prev, next) => {
					const prevValueName = getCompanyName(
						getCompanyIdByTaxiServiceId(prev.taxiServiceId),
					);

					const nextValueName = getCompanyName(
						getCompanyIdByTaxiServiceId(next.taxiServiceId),
					);

					const prevValue = prevValueName?.[lang] ?? "";
					const nextValue = nextValueName?.[lang] ?? "";
					return (
						sortByNumberOrString(prevValue, nextValue) * sortOrder
					);
				});

				return data;
			}

			if (sort.dataKey === ColumnId.TaxiService) {
				const data = items.sort((prev, next) => {
					getTaxiServiceName;

					const prevValueName = getTaxiServiceName(
						prev.taxiServiceId,
					);

					const nextValueName = getTaxiServiceName(
						next.taxiServiceId,
					);

					const prevValue = prevValueName?.[lang] ?? "";
					const nextValue = nextValueName?.[lang] ?? "";
					return (
						sortByNumberOrString(prevValue, nextValue) * sortOrder
					);
				});

				return data;
			}
		}

		itemsMap.clear();
		return items;
	}, [
		data,
		executors,
		filters,
		sort,
		lang,
		getCompanyIdByTaxiServiceId,
		getCompanyName,
		getTaxiServiceName,
		isSort,
	]);

	return (
		<StyledGrid areas="content footer" rows="1fr auto" w="100%" h="100%">
			<div
				ref={tableContainerRef}
				tabIndex={-1}
				onKeyDown={onKeyDown}
				onKeyUp={onKeyUp}
			>
				<CompactTable
					ref={tableRef}
					fillHeight
					virtualized
					headerHeight={STABILITY_HEIGHT_ROW}
					rowHeight={rowHeight}
					loading={loading}
					data={filterValue}
					rowClassName={tableRowClassName}
					shouldUpdateScroll={(name) =>
						name === "widthChanged" ? scrollPos : {}
					}
					onRowClick={onRowSelect}
					onRowDoubleClick={onRowDoubleClick}
					onScroll={onScroll}
					sortColumn={sort?.dataKey}
					sortType={sort?.sortType}
					onSortColumn={(
						dataKey: string,
						sortType: SortType | undefined,
					) => {
						if (["company", "taxiService"].includes(sort.dataKey)) {
							onChangeFilter({
								...filters,
								taxiServiceIdsPosition: [],
							});
							setIsSort(false);
						}
						onSort(dataKey, sortType);
					}}
					style={{
						overflowY: "auto",
						background: Colors.BACKGROUND_BLUE_1,
					}}
				>
					{renderedColumns}
				</CompactTable>
			</div>

			{tableFooter}

			<TableSettings
				ref={settingsRef}
				value={columnIds}
				defaultValue={defaultColumnIds}
				columns={columns}
				onChange={setColumnIds}
			/>
		</StyledGrid>
	);
};

declare namespace ModelTable {
	interface Column {
		id: LocalColumnId;
		width: number;
		visible: boolean;
	}

	interface Sort {
		column?: string;
		type?: SortType;
	}

	interface Props {
		selectedId: Nullable<number>;
		onChangeSelectedId: React.Dispatch<React.SetStateAction<number>>;
		onChangeIsEditModal: React.Dispatch<React.SetStateAction<boolean>>;
		onShowParkingModal: React.Dispatch<React.SetStateAction<boolean>>;

		loading: boolean;
		data: Parking.Model[];

		nameColuumnsGroup: ParkingsWidget.TabMain;
		//

		onEdit: Dispatch<Parking.Model>;
		editorTable: UseTableOptions["editor"];
		onChangeTable: UseTableOptions["onChange"];
		tableFooter: React.ReactNode;
	}

	interface ColumnContext {
		language: Language;
		t: UseTranslationResponse<"translation", undefined>[0];
		taxiServiceById: Record<number, TaxiService.Model>;
		width: number;
		onResize: (width?: number, columnId?: string) => void;
	}
}

export default ModelTable;
